import type {FC, ReactElement} from 'react'
import {Tooltip} from '@mui/material'
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import type {ScheduledConversationOutcome} from '@interfaces/alpinist/conversation'
import {useCustomTranslation} from '@hooks/useCustomTranslation.ts'

interface Props {
  /** Outcome of the seeding run; null/unknown falls back to the neutral badge. */
  outcome?: ScheduledConversationOutcome | string | null
}

/**
 * Differentiated status badge for a scheduled-prompt conversation:
 * - email_sent  -> green "email sent"
 * - no_email    -> neutral "no email" (agent succeeded but, by design, sent none)
 * - agent_failed-> red "agent could not complete"
 * - unknown     -> neutral scheduled badge (legacy rows from before this feature)
 */
const ScheduledStatusIcon: FC<Props> = ({outcome}): ReactElement => {
  const [t] = useCustomTranslation('alpinist')

  let icon: ReactElement
  let labelKey: string
  switch (outcome) {
    case 'email_sent':
      labelKey = 'conversations_scheduled_email_sent'
      icon = (
        <MarkEmailReadOutlinedIcon fontSize="small" sx={{flexShrink: 0, color: 'success.main'}} />
      )
      break
    case 'agent_failed':
      labelKey = 'conversations_scheduled_agent_failed'
      icon = <ErrorOutlineIcon fontSize="small" sx={{flexShrink: 0, color: 'error.main'}} />
      break
    case 'no_email':
      labelKey = 'conversations_scheduled_no_email'
      icon = <ScheduleSendIcon fontSize="small" sx={{flexShrink: 0, color: 'text.secondary'}} />
      break
    default:
      labelKey = 'conversations_scheduled_badge'
      icon = <ScheduleSendIcon fontSize="small" sx={{flexShrink: 0, color: 'text.secondary'}} />
  }

  const label = t(labelKey)
  return (
    <Tooltip title={label}>
      <span role="img" aria-label={label} style={{display: 'inline-flex', flexShrink: 0}}>
        {icon}
      </span>
    </Tooltip>
  )
}

export default ScheduledStatusIcon
